import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { Row, Col, Container } from "react-bootstrap";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCircleCheck, faPen, faXmark } from "@fortawesome/free-solid-svg-icons";
import { createFiscalAddress, getFiscalAddressList, deleteFiscalAddress, updateFiscalAddress } from "../actions/userActions";
import { updateRestaurant } from "../actions/restaurantActions";
import { ErrorMessageBox, ModernButton, ModalOverlay } from "../components/DefaultImports";
import { CustomModal } from "../components/CustomModal";
import { ConfirmationModal } from "../components/ConfirmationModal";
import InputMask from "react-input-mask";

/* ===== helpers ===== */
const normalizeValue = value => String(value || "").trim().toLowerCase();
const extractFiscalAddressIdFromResult = result => {
  return result?.id ?? result?.data?.id ?? result?.payload?.id ?? result?.payload?.data?.id ?? result?.payload?.fiscal_address?.id ?? result?.fiscal_address?.id ?? null;
};
const extractFiscalAddressListFromResult = result => {
  const possibleList = result?.payload?.results ?? result?.payload?.data ?? result?.payload ?? result?.data?.results ?? result?.data ?? [];
  return Array.isArray(possibleList) ? possibleList : [];
};
const findMatchingFiscalAddress = (list, payload) => {
  if (!Array.isArray(list)) return null;
  return list.find(item => {
    return normalizeValue(item?.name) === normalizeValue(payload?.name) && normalizeValue(item?.address) === normalizeValue(payload?.address) && normalizeValue(item?.postal_code) === normalizeValue(payload?.postal_code) && normalizeValue(item?.country) === normalizeValue(payload?.country) && normalizeValue(item?.fiscal_number) === normalizeValue(payload?.fiscal_number) && normalizeValue(item?.floor_number) === normalizeValue(payload?.floor_number);
  }) || null;
};

/* ====== styles ====== */
const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #99b0ad;
  margin-bottom: 8px;
`;
const Grid = styled(Row)`
  row-gap: 12px;
`;
const CardBox = styled.div`
  position: relative;
  width: 100%;
  padding: 14px;
  min-height: 150px;
  background: #ffffff;
  color: #1c1c1c;
  border: ${({
  isSelected
}) => isSelected ? "2px solid #de8c3a" : "1px solid #e7ecec"};
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.06);
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease,
    border-color 0.12s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  }

  cursor: ${({
  isClickable
}) => isClickable ? "pointer" : "default"};
`;
const AddCard = styled(CardBox)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: dashed;
  border-color: #99b0ad;
  color: #99b0ad;

  &:hover {
    border-color: #de8c3a;
    color: #de8c3a;
  }
`;
const BigPlus = styled(FontAwesomeIcon)`
  font-size: 46px;
`;
const CheckIcon = styled(FontAwesomeIcon)`
  position: absolute;
  font-size: 20px;
  left: 8px;
  top: 8px;
  color: #de8c3a;
`;
const SelectCircle = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  left: 8px;
  top: 8px;
  background-color: white;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
`;
const Actions = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
`;
const IconButton = styled.button`
  border: none;
  background: transparent;
  padding: 6px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #99b0ad;
  cursor: pointer;

  &:hover {
    background: #f5f7f7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
const Name = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: #334442;
  margin-bottom: 6px;
  padding-left: ${p => p.$hasSelector ? "28px" : "0"};
`;
const Line = styled.div`
  color: #556663;
  font-size: 14px;
  line-height: 1.35;
  padding-left: ${p => p.$hasSelector ? "28px" : "0"};
`;
const ScrollContainer = styled.div`
  width: 100%;
  max-height: 420px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  scrollbar-gutter: stable;
`;
const EmptyState = styled.div`
  background: #faf6e9;
  border: 1px dashed #e7d7bf;
  color: #de8c3a;
  padding: 14px;
  border-radius: 12px;
  text-align: center;
  font-weight: 600;
  margin-bottom: 10px;
`;
const InfoNote = styled.div`
  background: #f8fbfb;
  border: 1px solid #e7ecec;
  color: #556663;
  padding: 12px 14px;
  border-radius: 12px;
  margin-bottom: 12px;
  font-size: 14px;
`;

/* Simple variant styles */
const SimpleBlock = styled.div`
  margin-bottom: 18px;
`;
const SimpleRow = styled.div`
  font-size: 15px;
  line-height: 1.35;
  color: #334442;
  margin: 4px 0;
`;
const SimpleLabel = styled.span`
  font-weight: 700;
  color: #99b0ad;
  margin-right: 6px;
`;

/* form styles */
const StyledInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: 16px;
  border: 1px solid ${props => props.invalid ? "#dc3545" : "#ddd"};
  border-radius: 10px;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${props => props.invalid ? "#dc3545" : "#de8c3a"};
    background-color: #fff;
    box-shadow: ${props => props.invalid ? "0 0 0 4px rgba(220, 53, 69, 0.15)" : "0 0 0 4px rgba(222, 140, 58, 0.15)"};
    outline: none;
  }

  &::placeholder {
    color: #aaa;
  }
`;
const StyledMaskedInput = styled(InputMask)`
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 18px;
  font-size: 16px;
  border: 1px solid ${props => props.invalid ? "#dc3545" : "#ddd"};
  border-radius: 10px;
  background-color: #fafafa;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${props => props.invalid ? "#dc3545" : "#de8c3a"};
    background-color: #fff;
    box-shadow: ${props => props.invalid ? "0 0 0 4px rgba(220, 53, 69, 0.15)" : "0 0 0 4px rgba(222, 140, 58, 0.15)"};
    outline: none;
  }

  &::placeholder {
    color: #aaa;
  }
`;
const FieldLabel = styled.h5`
  font-size: 14px;
  font-weight: 700;
  color: #99b0ad;
  margin: 0 0 6px;
`;

/* ====== form ====== */
const FiscalAddressForm = ({
  user_id,
  restaurant_id,
  dispatch,
  closeModal,
  initialData = null,
  is_payment_management_panel = false
}) => {
  const {
    t
  } = useTranslation();
  const [formData, setFormData] = useState({
    id: null,
    user: user_id,
    name: "",
    address: "",
    postal_code: "",
    country: "",
    floor_number: "",
    fiscal_number: ""
  });
  const [error, setError] = useState("");
  const [invalidFields, setInvalidFields] = useState([]);
  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id ?? null,
        user: user_id,
        name: initialData.name || "",
        address: initialData.address || "",
        postal_code: initialData.postal_code || "",
        country: initialData.country || "",
        floor_number: initialData.floor_number || "",
        fiscal_number: initialData.fiscal_number || ""
      });
    } else {
      setFormData({
        id: null,
        user: user_id,
        name: "",
        address: "",
        postal_code: "",
        country: "",
        floor_number: "",
        fiscal_number: ""
      });
    }
  }, [initialData, user_id]);
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setFormData(s => ({
      ...s,
      [name]: value
    }));
    if (invalidFields.includes(name)) {
      setInvalidFields(prev => prev.filter(field => field !== name));
    }
  };
  const handleSubmit = async e => {
    e.preventDefault();
    const required = [{
      key: "name",
      label: t("ui.i18nFinalCleanup.name"),
      value: formData.name
    }, {
      key: "address",
      label: t("ui.i18nFinalCleanup.address"),
      value: formData.address
    }, {
      key: "postal_code",
      label: t("ui.i18nFinalCleanup.postalCode"),
      value: formData.postal_code
    }, {
      key: "country",
      label: t("ui.i18nFinalCleanup.country"),
      value: formData.country
    }, {
      key: "fiscal_number",
      label: "NIF",
      value: formData.fiscal_number
    }];
    const missing = required.filter(f => !String(f.value || "").trim());
    if (missing.length) {
      setInvalidFields(missing.map(f => f.key));
      return;
    }
    setError("");
    setInvalidFields([]);
    const payload = {
      user: user_id,
      name: formData.name.trim(),
      address: formData.address.trim(),
      postal_code: formData.postal_code.trim(),
      country: formData.country.trim(),
      floor_number: formData.floor_number?.trim() || "",
      fiscal_number: formData.fiscal_number.trim()
    };
    try {
      let savedFiscalAddressId = formData.id || null;
      let actionResult = null;
      if (formData.id) {
        actionResult = await dispatch(updateFiscalAddress(formData.id, payload));
        savedFiscalAddressId = extractFiscalAddressIdFromResult(actionResult) || formData.id;
      } else {
        actionResult = await dispatch(createFiscalAddress(user_id, payload));
        savedFiscalAddressId = extractFiscalAddressIdFromResult(actionResult);
      }
      const refreshResult = await dispatch(getFiscalAddressList(user_id));
      if (!savedFiscalAddressId) {
        const refreshedList = extractFiscalAddressListFromResult(refreshResult);
        const matchedAddress = findMatchingFiscalAddress(refreshedList, payload);
        savedFiscalAddressId = matchedAddress?.id || null;
      }
      if (is_payment_management_panel && savedFiscalAddressId && restaurant_id) {
        await dispatch(updateRestaurant(restaurant_id, {
          fiscal_information: savedFiscalAddressId
        }));
      }
      closeModal(savedFiscalAddressId);
    } catch (err) {
      setError(t("ui.i18nMessages.saveFiscalProfileError"));
      console.error("FiscalAddressForm submit error:", err);
    }
  };
  return <>
      {error && <ErrorMessageBox style={{
      fontSize: "0.9rem",
      marginBottom: 10
    }}>
          {error}
        </ErrorMessageBox>}

      <form id="fiscalAddressForm" onSubmit={handleSubmit}>
        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.nome")}</FieldLabel>
          <StyledInput type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t("ui.screens.fiscalAdress.nomeFiscal")} invalid={invalidFields.includes("name")} />
        </div>

        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.nifNumeroDeIdentificacaoFiscal")}</FieldLabel>
          <StyledInput type="text" name="fiscal_number" value={formData.fiscal_number} onChange={handleInputChange} placeholder="123456789" invalid={invalidFields.includes("fiscal_number")} />
        </div>

        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.morada")}</FieldLabel>
          <StyledInput type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder={t("ui.screens.fiscalAdress.moradaFiscal")} invalid={invalidFields.includes("address")} />
        </div>

        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.codigoPostal")}</FieldLabel>
          <StyledMaskedInput mask="9999-999" placeholder="____-___" maskChar="_" value={formData.postal_code} onChange={handleInputChange} name="postal_code" invalid={invalidFields.includes("postal_code")} />
        </div>

        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.pais")}</FieldLabel>
          <StyledInput type="text" name="country" value={formData.country} onChange={handleInputChange} placeholder={t("ui.screens.fiscalAdress.pais2")} invalid={invalidFields.includes("country")} />
        </div>

        <div className="form-group">
          <FieldLabel>{t("ui.screens.fiscalAdress.informacaoAdicionalOpcional")}</FieldLabel>
          <StyledInput type="text" name="floor_number" value={formData.floor_number} onChange={handleInputChange} placeholder={t("ui.screens.fiscalAdress.andarPortaBloco")} />
        </div>

        <div className="modal-footer" style={{
        padding: 0
      }}>
          <ModernButton color="#de8c3a" fontSize="14px" type="submit">{t("ui.screens.fiscalAdress.guardar")}</ModernButton>
        </div>
      </form>
    </>;
};

/* ====== main ====== */
function FiscalAdress({
  isItSelectable = false,
  currentSelected = null,
  setSelected = false,
  showTitle = true,
  cardsPerRow = 2,
  variant = "default",
  fiscal_adress_id = null,
  is_payment_management_panel = false
}) {
  const {
    t
  } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState(null);
  const [editingAddress, setEditingAddress] = useState(null);
  const [paymentSelectedId, setPaymentSelectedId] = useState(fiscal_adress_id || null);
  const [linkingError, setLinkingError] = useState("");
  const [isLinkingFiscal, setIsLinkingFiscal] = useState(false);
  const dispatch = useDispatch();
  const {
    userInfo
  } = useSelector(state => state.userLogin);
  const {
    getFiscalAdress
  } = useSelector(state => state.fiscalAddressStore);
  const restaurantDetails = useSelector(state => state.restaurantDetails);
  const {
    restaurants,
    isLoading,
    error
  } = restaurantDetails;
  const fiscalAddresses = Array.isArray(getFiscalAdress) ? getFiscalAdress : [];
  const linkedFiscalAddress = fiscal_adress_id != null ? fiscalAddresses.find(addr => String(addr.id) === String(fiscal_adress_id)) : null;
  const paymentPickerMode = is_payment_management_panel && (!fiscal_adress_id || !linkedFiscalAddress);
  const displayedAddresses = is_payment_management_panel ? paymentPickerMode ? fiscalAddresses : linkedFiscalAddress ? [linkedFiscalAddress] : [] : fiscalAddresses;
  const hasAnyToDisplay = displayedAddresses.length > 0;
  useEffect(() => {
    if (userInfo?.id) {
      dispatch(getFiscalAddressList(userInfo.id));
    }
  }, [dispatch, userInfo?.id]);
  useEffect(() => {
    setPaymentSelectedId(fiscal_adress_id || null);
  }, [fiscal_adress_id]);
  useEffect(() => {
    if (is_payment_management_panel) return;
    if (fiscalAddresses.length > 0 && setSelected) {
      setSelected(fiscalAddresses[0]?.id);
    }
  }, [fiscalAddresses, setSelected, is_payment_management_panel]);
  const handleRegularSelection = value => {
    if (isItSelectable && setSelected) {
      setSelected(value);
    }
  };
  const handlePaymentSelection = async addrId => {
    if (!is_payment_management_panel || !paymentPickerMode) return;
    if (!restaurants) {
      setLinkingError(t("ui.i18nFinalCleanup.couldNotIdentifyRestaurant"));
      return;
    }
    try {
      setIsLinkingFiscal(true);
      setLinkingError("");
      await dispatch(updateRestaurant(restaurants.id, {
        fiscal_information: addrId
      }));
      setPaymentSelectedId(addrId);
    } catch (err) {
      setLinkingError(t("ui.i18nFinalCleanup.linkFiscalProfileError"));
      console.error("handlePaymentSelection error:", err);
    } finally {
      setIsLinkingFiscal(false);
    }
  };
  const handleCardClick = addrId => {
    if (is_payment_management_panel) {
      handlePaymentSelection(addrId);
      return;
    }
    handleRegularSelection(addrId);
  };
  const openCreate = () => {
    setEditingAddress(null);
    setShowModal(true);
  };
  const openEdit = addr => {
    setEditingAddress(addr);
    setShowModal(true);
  };
  const handleDeleteClick = (e, id) => {
    e.stopPropagation();
    setAddressToDelete(id);
    setShowConfirmDelete(true);
  };
  const confirmDelete = async () => {
    if (!addressToDelete) return;
    await dispatch(deleteFiscalAddress(addressToDelete));
    await dispatch(getFiscalAddressList(userInfo.id));
    setShowConfirmDelete(false);
    setAddressToDelete(null);
  };
  const colSpan = Math.max(1, Math.min(12, Math.floor(12 / cardsPerRow)));
  const currentVisualSelectedId = is_payment_management_panel ? paymentSelectedId : currentSelected;
  const shouldShowSelector = isItSelectable || is_payment_management_panel && hasAnyToDisplay;
  const titleText = is_payment_management_panel ? t("ui.i18nFinalCleanup.fiscalProfile") : "Perfis fiscais";
  const showAddCard = variant === "default" && (!is_payment_management_panel && hasAnyToDisplay || is_payment_management_panel && paymentPickerMode);
  const showSimpleAddButton = variant === "simple" && is_payment_management_panel && paymentPickerMode;
  return <Container>
      {showTitle && <Title>{titleText}</Title>}

      {linkingError && <ErrorMessageBox style={{
      fontSize: "0.9rem",
      marginBottom: 10
    }}>
          {linkingError}
        </ErrorMessageBox>}

      {showConfirmDelete && <ModalOverlay />}

      {is_payment_management_panel && paymentPickerMode && <InfoNote>{t("ui.screens.fiscalAdress.escolheUmPerfilFiscalExistentePara")}</InfoNote>}

      {!hasAnyToDisplay && <>
          <EmptyState>
            {is_payment_management_panel ? t("ui.i18nCleanup2.esteRestauranteAindaNaoTemNenhumPerfilFiscalAssociado") : t("ui.i18nCleanup2.aindaNaoTensNenhumPerfilFiscalCriado")}
          </EmptyState>

          <Grid>
            <Col xs={12} sm={colSpan}>
              <AddCard isClickable onClick={e => {
            e.stopPropagation();
            openCreate();
          }}>
                <BigPlus icon={faPlus} />
              </AddCard>
            </Col>
          </Grid>
        </>}

      {hasAnyToDisplay && <ScrollContainer>
          <Grid>
            {displayedAddresses.map(addr => {
          const hasSelector = shouldShowSelector;
          const isSelected = String(currentVisualSelectedId) === String(addr.id);
          const isClickable = !is_payment_management_panel && !!isItSelectable || is_payment_management_panel && paymentPickerMode;
          return <Col key={addr.id} xs={12} sm={colSpan} onClick={() => handleCardClick(addr.id)}>
                  {variant === "simple" ? <SimpleBlock>
                      <SimpleRow>
                        <SimpleLabel>{t("ui.screens.fiscalAdress.nome2")}</SimpleLabel>
                        {addr.name || "—"}
                      </SimpleRow>
                      <SimpleRow>
                        <SimpleLabel>{t("ui.screens.fiscalAdress.nif")}</SimpleLabel>
                        {addr.fiscal_number || "—"}
                      </SimpleRow>
                      <SimpleRow>
                        <SimpleLabel>{t("ui.screens.fiscalAdress.morada2")}</SimpleLabel>
                        {addr.address || "—"}
                      </SimpleRow>
                      <SimpleRow>
                        <SimpleLabel>{t("ui.screens.fiscalAdress.codigoPostal2")}</SimpleLabel>
                        {addr.postal_code || "—"}
                      </SimpleRow>
                      <SimpleRow>
                        <SimpleLabel>{t("ui.screens.fiscalAdress.pais3")}</SimpleLabel>
                        {addr.country || "—"}
                      </SimpleRow>

                      {addr.floor_number ? <SimpleRow>
                          <SimpleLabel>{t("ui.screens.fiscalAdress.infoAdicional")}</SimpleLabel>
                          {addr.floor_number}
                        </SimpleRow> : null}

                      <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 8,
                flexWrap: "wrap"
              }}>
                        {is_payment_management_panel && paymentPickerMode && <ModernButton color={isSelected ? "#99b0ad" : "#de8c3a"} fontSize="13px" disabled={isLinkingFiscal || isSelected} onClick={e => {
                  e.stopPropagation();
                  handlePaymentSelection(addr.id);
                }}>
                            {isSelected ? "Selecionado" : t("ui.i18nCleanup.select")}
                          </ModernButton>}

                        <ModernButton color="#de8c3a" fontSize="13px" onClick={e => {
                  e.stopPropagation();
                  openEdit(addr);
                }}>{t("ui.screens.fiscalAdress.editar")}</ModernButton>
                      </div>
                    </SimpleBlock> : <CardBox isClickable={isClickable} isSelected={isSelected}>
                      {hasSelector && <SelectCircle />}
                      {hasSelector && isSelected && <CheckIcon icon={faCircleCheck} />}

                      <Actions>
                        <IconButton title={t("ui.screens.fiscalAdress.editar2")} aria-label={t("ui.screens.fiscalAdress.editar3")} onClick={e => {
                  e.stopPropagation();
                  openEdit(addr);
                }}>
                          <FontAwesomeIcon icon={faPen} />
                        </IconButton>

                        {!is_payment_management_panel && <IconButton title={t("ui.screens.fiscalAdress.remover")} aria-label={t("ui.screens.fiscalAdress.remover2")} onClick={e => handleDeleteClick(e, addr.id)}>
                            <FontAwesomeIcon icon={faXmark} />
                          </IconButton>}
                      </Actions>

                      <Name $hasSelector={hasSelector}>{addr.name}</Name>
                      <Line $hasSelector={hasSelector}>{addr.address}</Line>
                      <Line $hasSelector={hasSelector}>{addr.postal_code}</Line>
                      <Line $hasSelector={hasSelector}>{addr.country}</Line>
                      <Line $hasSelector={hasSelector}>
                        {addr.fiscal_number}
                      </Line>
                    </CardBox>}
                </Col>;
        })}

            {showAddCard && <Col xs={12} sm={colSpan}>
                <AddCard isClickable onClick={e => {
            e.stopPropagation();
            openCreate();
          }}>
                  <BigPlus icon={faPlus} />
                </AddCard>
              </Col>}
          </Grid>

          {showSimpleAddButton && <div style={{
        marginTop: 12
      }}>
              <ModernButton color="#de8c3a" fontSize="14px" onClick={openCreate}>{t("ui.screens.fiscalAdress.adicionarPerfilFiscal")}</ModernButton>
            </div>}
        </ScrollContainer>}

      <CustomModal show={showModal} onHide={() => {
      setShowModal(false);
      setEditingAddress(null);
    }} title={editingAddress ? t("ui.i18nCleanup2.editarPerfilFiscal") : t("ui.i18nCleanup2.novoPerfilFiscal")} centered={false}>
        {userInfo?.id && <FiscalAddressForm user_id={userInfo.id} restaurant_id={restaurants?.id} dispatch={dispatch} closeModal={savedFiscalAddressId => {
        if (is_payment_management_panel && savedFiscalAddressId && paymentPickerMode) {
          setPaymentSelectedId(savedFiscalAddressId);
        }
        setShowModal(false);
        setEditingAddress(null);
      }} initialData={editingAddress} is_payment_management_panel={is_payment_management_panel} />}
      </CustomModal>

      {!is_payment_management_panel && <ConfirmationModal show={showConfirmDelete} onHide={() => setShowConfirmDelete(false)} onConfirm={confirmDelete} title={t("ui.screens.fiscalAdress.removerPerfilFiscal")} body={t("ui.i18nCleanup.removeFiscalProfileConfirm")} confirmLabel={t("ui.screens.fiscalAdress.remover")} confirmColor="#ff704d" cancelLabel={t("common.cancel")} cancelColor="#99b0ad" centered={false} topSpacing={"250px auto"} />}
    </Container>;
}
export default FiscalAdress;
